/**
 * Utility functions for formatting and manipulating price displays.
 */

/**
 * Rounds up any calculated price amount to the nearest tenth (one decimal place),
 * regardless of the value of the subsequent digits.
 * If the resulting value is an integer or doesn't have subsequent digits after the tenth place,
 * trailing zeroes (like .0) are omitted in the display representation.
 *
 * Examples:
 *   4.11 -> 4.2
 *   4.19 -> 4.2
 *   432.833 -> 432.9
 *   7.001 -> 7.1
 *   5.30 -> 5.3
 *   100 -> 100
 *
 * @param value The price amount as a number or string.
 * @returns The rounded up price as a number.
 */
export function roundUpPrice(value: number | string): number {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return 0;

  // To prevent floating-point representation issues (e.g. 5.3 * 10 = 53.00000000000001
  // which Math.ceil would mistakenly round up to 54), we use toFixed(10) first to format
  // to a high precision, then do the Math.ceil round up on the tenth's place.
  const rounded = Math.ceil(parseFloat(num.toFixed(10)) * 10) / 10;
  return rounded;
}
